import { useMemo } from 'react';
import { HOUSEHOLD_DOC, HOUSEHOLD_MEMBERS_KEY, MEMBER_COLORS } from '@awwyeah/shared';
import type { HouseholdMember } from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';

/**
 * Who tapped what. Same convention as `lists`/`checkin`/`digest`: each feature module keeps
 * its own small roster reader rather than importing another module's copy, and only ever
 * **reads** the `household` doc. A member's colour is their own stored profile hex (see
 * docs/DECISIONS.md 2026-08-01), not a token assigned by roster position.
 */

export interface RosterMember {
  id: string;
  name: string;
  color: string;
  avatarEmoji?: string;
}

function fallbackColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_COLORS[hash % MEMBER_COLORS.length] as string;
}

export interface TrackerRoster {
  meId: string | null;
  getMember: (id: string) => RosterMember;
  /** First name only — the pills are narrow and "Dan" beats "Dan Bonomo" under a button. */
  shortName: (id: string) => string;
}

export function useHouseholdRoster(): TrackerRoster {
  const { user } = useUser();
  const doc = useYDoc(HOUSEHOLD_DOC);
  const { entries } = useYMap<HouseholdMember>(doc, HOUSEHOLD_MEMBERS_KEY);

  const members = useMemo(() => {
    const map = new Map<string, RosterMember>();
    for (const [id, member] of entries) {
      if (!member?.name) continue;
      map.set(id, {
        id,
        name: member.name,
        color: member.color,
        ...(member.avatarEmoji ? { avatarEmoji: member.avatarEmoji } : {}),
      });
    }
    if (user && !map.has(user.id)) {
      map.set(user.id, {
        id: user.id,
        name: user.name,
        color: user.color,
        ...(user.avatarEmoji ? { avatarEmoji: user.avatarEmoji } : {}),
      });
    }
    return map;
  }, [entries, user]);

  const getMember = (id: string): RosterMember =>
    members.get(id) ?? {
      id,
      name: id === user?.id ? user.name : 'Someone',
      color: fallbackColor(id),
    };

  return {
    meId: user?.id ?? null,
    getMember,
    shortName: (id) => getMember(id).name.trim().split(/\s+/)[0] ?? 'Someone',
  };
}
